import { refs, booleanDataset, throttle, domReady } from '../modules/utils.js';
import { test as testMedia } from '../modules/match-media.js';
import gsap from 'gsap';

class FullSlider extends HTMLElement {
  static get observedAttributes() {
    return ['data-index'];
  }

  get indexMax() {
    if (this.refs.slides) {
      return this.refs.slides.length - 1;
    }
    return -1;
  }

  get indexNext() {
    const index = parseInt(this.dataset.index);
    return (index + 1 > this.indexMax) ? 0 : index + 1;
  }

  get indexPrev() {
    const index = parseInt(this.dataset.index);
    return (index - 1 < 0) ? this.indexMax : index - 1;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-index') {
      newValue = parseInt(newValue);
      oldValue = parseInt(oldValue);
      const isLastSlide = this.refs.bar.children[this.refs.bar.children.length - 1].dataset.index === `${newValue}`;
      const isFirstSlide = this.refs.bar.children[0].dataset.index === `${newValue}`;
      if (isLastSlide) {
        this.#moveFirstSlideToEnd(oldValue);
      }
      // ignore first time
      if (isFirstSlide && !isNaN(oldValue)) {
        this.#moveLastSlideToFirst(oldValue);
      }
      this.stopInteractions = true;

      clearTimeout(this.timeout);
      this.tweenTimeoutBar?.pause(); // Pause the animation

      Promise.all([
        this.#slideTo(newValue),
        this.#updateIndex(),
        this.#updateLabel()
      ]).then(() => {
        this.stopInteractions = false;
        this.#setTimeout();
      });
    }
  }

  async connectedCallback() {
    await domReady();
    this.refs = refs(this);
    this.#listeners();
    this.#init();
  }

  #listeners() {
    // scroll listener
    this.addEventListener('wheel', throttle((e) => {
      if (this.stopInteractions) return;
      const absDeltaY = Math.abs(e.deltaY);
      if (absDeltaY > 10) {
        if (e.deltaY > 0) {
          this.dataset.index = this.indexNext;
        } else {
          this.dataset.index = this.indexPrev;
        }
      }
    }, 200));

    // touch events
    let touchStartY = 0;
    let touchEndY = 0;

    this.addEventListener('touchstart', (e) => {
      e.preventDefault();
      touchStartY = e.changedTouches[0].screenY;
    });

    this.addEventListener('touchend', (e) => {
      e.preventDefault();
      if (this.stopInteractions) return;

      touchEndY = e.changedTouches[0].screenY;
      const touchDiff = touchStartY - touchEndY;

      // Only respond to significant vertical swipes (threshold: 50px)
      if (Math.abs(touchDiff) > 50) {
        if (touchDiff > 0) {
          // Swipe up - go to next slide
          this.dataset.index = this.indexNext;
        } else {
          // Swipe down - go to previous slide
          this.dataset.index = this.indexPrev;
        }
      }
    });

    // prevent bounce
    this.addEventListener('wheel', (e) => e.preventDefault(), { passive: false });
  }

  #init() {
    this.dataset.index = 0;
  }

  #setTimeout() {
    this.tweenTimeoutBar?.kill();

    const duration = 3;

    this.timeout = setTimeout(() => {
      this.dataset.index = this.indexNext;
    }, duration * 1000);

    this.tweenTimeoutBar = gsap.fromTo(
      this.refs.timeoutBar,
      {
        scaleX: 0
      },
      {
        scaleX: 1,
        duration,
        ease: 'none'
      }
    );
  }

  async #slideTo(index) {
    const slideEl = this.refs.slides.filter((el) => parseInt(el.dataset.index) === index)[0];
    const slideElIndex = this.refs.slides.indexOf(slideEl);

    if (!slideEl) return;

    if (testMedia('small')) {
      const y = `${slideElIndex * -66.6666}dvh`;
      return gsap.to(
        this.refs.bar,
        {
          y,
          duration: 1,
          ease: 'power2.inOut'
        }
      ).then();
    }
    else {
      const x = `${slideElIndex * -50}rem`;
      return gsap.to(
        this.refs.bar,
        {
          x,
          duration: 1,
          ease: 'power2.inOut'
        }
      ).then();
    }
  }

  async #updateIndex() {
    const index = parseInt(this.dataset.index) + 1;
    const indexStr = `${index}`.toString().padStart(2, '0');
    const tl = gsap.timeline({
      onComplete: () => { this.refs.indexLabelCurrent.textContent = indexStr; }
    });

    this.refs.indexLabelNext.textContent = indexStr;
    const duration = 0.5;

    tl.to(
      this.refs.indexLabelCurrent,
      {
        yPercent: -100,
        duration,
        ease: 'power2.out',
        clearProps: 'all'
      },
      0
    );

    tl.to(
      this.refs.indexLabelNext,
      {
        yPercent: -100,
        duration,
        ease: 'power2.out',
        clearProps: 'all'
      },
      0
    );

    return tl.then();
  }

  async #updateLabel() {
    const index = parseInt(this.dataset.index);
    const labelEl = this.refs.labels[index];
    const labelPrevEl = this.refs.labels.filter((el) => el.dataset.hasOwnProperty('display'))[0];
    const tl = gsap.timeline({});

    booleanDataset(labelEl, 'display', true);

    if (labelPrevEl) {
      tl.to(
        labelPrevEl.querySelectorAll('h6'),
        {
          yPercent: -100,
          duration: 0.5,
          ease: 'power2.in',
          stagger: 0.1,
          onComplete: () => {
            booleanDataset(labelPrevEl, 'display', false);
          }
        }
      );
    }

    tl.fromTo(
      labelEl.querySelectorAll('h6'),
      {
        yPercent: 100
      },
      {
        yPercent: 0,
        duration: 0.5,
        ease: 'power2.out',
        stagger: 0.1
      }
    );

    return tl.then();
  }

  #moveFirstSlideToEnd(oldIndex) {
    const firstSlide = this.refs.bar.firstElementChild;
    this.refs.bar.appendChild(firstSlide);
    this.refs.slides = Array.from(this.refs.bar.querySelectorAll('[data-ref="[slides]"]'));
    const slideEl = this.refs.slides.filter((el) => parseInt(el.dataset.index) === oldIndex)[0];
    const slideElIndex = this.refs.slides.indexOf(slideEl);
    if (testMedia('small')) {
      const y = `${slideElIndex * -66.6666}dvh`;
      gsap.set(
        this.refs.bar,
        {
          y,
          duration: 0
        }
      );
    }
    else {
      const x = `${slideElIndex * -50}rem`;
      gsap.set(
        this.refs.bar,
        {
          x,
          duration: 0
        }
      );
    }
  }

  #moveLastSlideToFirst(oldIndex) {
    const lastSlide = this.refs.bar.lastElementChild;
    this.refs.bar.prepend(lastSlide);
    this.refs.slides = Array.from(this.refs.bar.querySelectorAll('[data-ref="[slides]"]'));
    const slideEl = this.refs.slides.filter((el) => parseInt(el.dataset.index) === oldIndex)[0];
    const slideElIndex = this.refs.slides.indexOf(slideEl);
    if (testMedia('small')) {
      const y = `${slideElIndex * -66.6666}dvh`;
      gsap.set(
        this.refs.bar,
        {
          y,
          duration: 0
        }
      );
    }
    else {
      const x = `${slideElIndex * -50}rem`;
      gsap.set(
        this.refs.bar,
        {
          x,
          duration: 0
        }
      );
    }
  }
}

customElements.define('full-slider', FullSlider);
